// Context builder + renderer for admin-authored notification templates.
//
// CONTRACT ARTIFACT — mirrors the admin token catalogs (bignerve-admin
// template-catalog.js + highlights "FRONTEND LINKS"); change both sides together.
// Unresolved tokens stay literal so authors can spot them.

import Handlebars from 'handlebars';
import { config } from '/app/config';
import { DEFAULT_PLATFORM, resolvePlatform } from '/app/lib/platform';

// Normalized entity view: hydrated `contexts` win — both highlights
// (hydrateHighlight) and neurons (hydrateNeuron) build them from
// `template_vars` — and legacy flat fields fill the rest.
function entitiesOf (item) {
    const contexts = item.contexts || {};

    const challenge = contexts.challenge
        || ((item.challenge_slug || item.challenge_title) && {
            slug: item.challenge_slug,
            title: item.challenge_title,
        });
    const idea = contexts.idea
        || (item.idea_slug && { slug: item.idea_slug });

    return {
        ...contexts,
        // Keys stay absent when unresolved, so a caller's own fallback survives.
        ...(challenge && { challenge }),
        ...(idea && { idea }),
    };
}

// Topic entity behind a forum event: keyed `topic` when the event is about one,
// `context` when the topic is only the surrounding container.
function forumTopicOf (entities) {
    return [entities.topic, entities.context].find((entity) => entity?.context_type === 'topic');
}

// Forum links on a system platform address the slug-less forum path, mirroring
// the forum cards (RecentMessageCard).
function forumPlatformSlug (topic) {
    const platform = topic?.platform;

    if (!platform?.slug || platform.type === 'system-platform') return undefined;

    return platform.slug;
}

// Origin a system sub-platform is served from (fiction.bignerve.com), mirroring
// the cross-domain links in the forum cards (ContextInfoCard) and OlympiadLink:
// the link's PlatformURL when delivered, else the known platform's url, else
// the app host with the platform slug as subdomain; the app host itself for
// the main platform. Undefined for user platforms.
function platformOrigin (platform) {
    if (platform?.type !== 'system-platform' || !platform.slug) return undefined;
    if (platform.slug === DEFAULT_PLATFORM) return config.app.baseURL;

    const url = platform.url || resolvePlatform(platform.slug)?.url;

    if (url) return url;

    try {
        const base = new URL(config.app.baseURL);
        base.hostname = `${platform.slug}.${base.hostname.replace(/^www\./, '')}`;

        return base.origin;
    } catch (e) {
        return undefined;
    }
}

// Route tokens, keyed as authored: 'route.' + route name. Resolvers return a
// route location, or falsy when required entities are missing (token stays
// literal). A location may carry an `origin` for routes served from a
// sub-platform host. Query variants go in the template:
// {route.challenges.show}?tab=ideas.
const ROUTE_TOKENS = {
    'route.challenges.show' ({ entities }) {
        if (!entities.challenge?.slug) return null;

        return {
            name: 'challenges.show',
            params: { challenge: entities.challenge.slug },
        };
    },
    'route.challenges.rate' ({ entities }) {
        if (!entities.challenge?.slug) return null;

        return {
            name: 'challenges.rate',
            params: { challenge: entities.challenge.slug },
        };
    },
    // The :id param becomes ?idea=<id>, matched against idea.id — context id, not slug.
    'route.challenges.show.idea' ({ entities }) {
        const id = entities.idea?.context_id || entities.idea?.id;

        if (!entities.challenge?.slug || !id) return null;

        return {
            name: 'challenges.show.idea',
            params: { challenge: entities.challenge.slug, id },
        };
    },
    'route.dashboard.innovator.challenges.open.detail.idea' ({ entities, platformSlug }) {
        const idea = entities.idea?.context_id || entities.idea?.id;

        if (!entities.challenge?.slug || !idea) return null;

        return {
            name: 'dashboard.innovator.challenges.open.detail.idea',
            params: { platformSlug, challenge: entities.challenge.slug, idea },
        };
    },
    // The payload's olympiad when it carries one — on its own platform, and on
    // its platform host for system platforms ('platform' resolves to the
    // host's subdomain there); the viewer's platform, latest olympiad, otherwise.
    'route.platform.olympiad.leaderboard' ({ entities, platformSlug }) {
        const olympiad = entities.olympiad;
        const id = olympiad?.context_id || olympiad?.id;
        const origin = platformOrigin(olympiad?.platform);

        return {
            name: 'platform.olympiad.leaderboard',
            params: {
                platformSlug: origin ? 'platform' : olympiad?.platform?.slug || platformSlug,
            },
            ...(id && { query: { olympiad: id } }),
            origin,
        };
    },
    // Subject user's profile when the payload carries one; the viewer's otherwise.
    'route.home.user' ({ entities, platformSlug }) {
        const person = Object.values(entities).find((entity) => entity?.context_type === 'user');
        const handle = person?.screen_name || person?.handle || person?.slug || platformSlug;

        return {
            name: 'home.user',
            params: { platformSlug: handle },
        };
    },
    'route.dashboard' ({ platformSlug }) {
        return { name: 'dashboard', params: { platformSlug } };
    },
    'route.dashboard.notifications' ({ platformSlug }) {
        return { name: 'dashboard.notifications', params: { platformSlug } };
    },
    'route.dashboard.catalyst' ({ platformSlug }) {
        return { name: 'dashboard.catalyst', params: { platformSlug } };
    },
    // Catalyst question detail tabs — the :challenge param is the slug.
    'route.dashboard.catalyst.detail.ideas' ({ entities, platformSlug }) {
        if (!entities.challenge?.slug) return null;

        return {
            name: 'dashboard.catalyst.detail.ideas',
            params: { platformSlug, challenge: entities.challenge.slug },
        };
    },
    'route.dashboard.catalyst.detail.mentoring' ({ entities, platformSlug }) {
        if (!entities.challenge?.slug) return null;

        return {
            name: 'dashboard.catalyst.detail.mentoring',
            params: { platformSlug, challenge: entities.challenge.slug },
        };
    },
    'route.dashboard.innovator' ({ platformSlug }) {
        return { name: 'dashboard.innovator', params: { platformSlug } };
    },
    'route.dashboard.rater' ({ platformSlug }) {
        return { name: 'dashboard.rater', params: { platformSlug } };
    },
    'route.dashboard.mentoring' ({ platformSlug }) {
        return { name: 'dashboard.mentoring', params: { platformSlug } };
    },
    'route.dashboard.director' ({ platformSlug }) {
        return { name: 'dashboard.director', params: { platformSlug } };
    },
    'route.dashboard.money-prizes' ({ platformSlug }) {
        return { name: 'dashboard.money-prizes', params: { platformSlug } };
    },
    'route.dashboard.my-progress.score-stats' ({ platformSlug }) {
        return { name: 'dashboard.my-progress.score-stats', params: { platformSlug } };
    },
    'route.home.olympiads' ({ platformSlug }) {
        return { name: 'home.olympiads', params: { platformSlug } };
    },
    // Innovator AMA suggestion-box idea detail — :challenge is the slug.
    'route.dashboard.innovator.suggestion-box.ideas.detail' ({ entities, platformSlug }) {
        const idea = entities.idea?.context_id || entities.idea?.id;

        if (!entities.challenge?.slug || !idea) return null;

        return {
            name: 'dashboard.innovator.suggestion-box.ideas.detail',
            params: { platformSlug, challenge: entities.challenge.slug, idea },
        };
    },
    'route.dashboard.catalyst.detail.mentoring.idea' ({ entities, platformSlug }) {
        const idea = entities.idea?.context_id || entities.idea?.id;

        if (!entities.challenge?.slug || !idea) return null;

        return {
            name: 'dashboard.catalyst.detail.mentoring.idea',
            params: { platformSlug, challenge: entities.challenge.slug, idea },
        };
    },
    'route.dashboard.director.current-olympiad' ({ platformSlug }) {
        return { name: 'dashboard.director.current-olympiad', params: { platformSlug } };
    },
    'route.dashboard.director.administration' ({ platformSlug }) {
        return { name: 'dashboard.director.administration', params: { platformSlug } };
    },
    'route.dashboard.suggestion-box' ({ platformSlug }) {
        return { name: 'dashboard.suggestion-box', params: { platformSlug } };
    },
    // Forum tokens read the topic link delivered in template_vars
    // (Link.PlatformSlug / Link.ChannelSlug).
    'route.discussion-forums' ({ entities, platformSlug }) {
        const topic = forumTopicOf(entities);

        return {
            name: 'discussion-forums.topics.all',
            params: { platformSlug: topic ? forumPlatformSlug(topic) : platformSlug },
            origin: platformOrigin(topic?.platform),
        };
    },
    'route.discussion-forums.topics.view' ({ entities }) {
        const topic = forumTopicOf(entities);
        const topicId = topic?.context_id || topic?.id;

        if (!topicId) return null;

        const platformSlug = forumPlatformSlug(topic);
        const channelSlug = topic.channel?.slug;
        const origin = platformOrigin(topic.platform);

        // 'topics' is the default channel and has its own channel-less route.
        if (!channelSlug || channelSlug === 'topics') {
            return { name: 'discussion-forums.topics.view', params: { platformSlug, topicId }, origin };
        }

        return {
            name: 'discussion-forums.channel.topics.view',
            params: { platformSlug, channelSlug, topicId },
            origin,
        };
    },
};

// Resolve every route token to a final href, keyed flat ('route.challenges.show').
function resolveRoutes (item, { router, platformSlug }) {
    const entities = entitiesOf(item);

    const routes = {};
    for (const [token, toLocation] of Object.entries(ROUTE_TOKENS)) {
        const location = toLocation({ item, entities, platformSlug });

        if (!location) continue;

        const { origin, ...to } = location;

        try {
            const href = router.resolve(to).href;

            routes[token] = origin && new URL(origin).host !== globalThis.location?.host
                ? `${origin.replace(/\/$/, '')}${href}`
                : href;
        } catch (e) {
            // Unresolvable route -> token stays literal.
        }
    }

    return routes;
}

export function buildContext (detail, { router, user }) {
    const platformSlug = user?.screen_name || 'me';

    return {
        points: detail.points,
        role: detail.role,
        user: user && {
            name: [user.first_name, user.last_name].filter(Boolean).join(' ') || undefined,
            handle: user.handle || user.screen_name,
        },
        // Legacy flat enrichment — only reached when the payload carries no
        // matching template_vars entity.
        catalyst: (detail.catalyst_name || detail.catalyst_handle) && {
            name: detail.catalyst_name,
            handle: detail.catalyst_handle,
        },
        idea: detail.idea_title && { title: detail.idea_title },
        olympiad: detail.olympiad_name && { name: detail.olympiad_name },
        // Hydrated entities ({challenge.title}, {catalyst.name}, {vote.title}, …).
        ...entitiesOf(detail),
        ...resolveRoutes(detail, { router, platformSlug }),
    };
}

// Hydrated highlight context: resolved entities ({catalyst.name}, ...) plus
// the flat route tokens.
export function buildHighlightContext (detail, { router, user }) {
    const platformSlug = user?.screen_name || 'me';

    return {
        role: detail.role,
        user: user && {
            name: [user.first_name, user.last_name].filter(Boolean).join(' ') || undefined,
            handle: user.handle || user.screen_name,
        },
        ...(detail.contexts || {}),
        // brain.* rides on the event payload, not template_vars.
        brain: detail.brain,
        ...resolveRoutes(detail, { router, platformSlug }),
    };
}

// Final markdown a neuron renders, or null when its admin template yields
// nothing. Shared by the list item and useNeurons, which collapses runs of
// identical statements.
export function renderNeuron (detail, { router, user }) {
    const message = resolveTemplate(detail.template, detail);

    if (!message) return null;

    return renderTemplate(message, {
        ...buildContext(detail, { router, user }),
        // {link} is deliberately not wired to the resolved neuron route:
        // it renders as "/" so a template still using it is noticeable —
        // fix the admin string to a route.* token instead.
        link: '/',
    });
}

// Final markdown a highlight renders, or null when its admin template yields
// nothing. Shared by the list item and useHighlights, which collapses runs of
// identical statements.
export function renderHighlight (detail, { router, user }) {
    const message = resolveTemplate(detail.template, detail);

    if (!message) return null;

    return renderTemplate(message, buildHighlightContext(detail, { router, user }));
}

// Context key matching `key`, exact first, then ignoring case — authors type
// {olympiad.Title} for {olympiad.title}.
function keyOf (context, key) {
    if (key in context) return key;

    const lower = key.toLowerCase();

    return Object.keys(context).find((k) => k.toLowerCase() === lower);
}

// Exact key first ('route.challenges.show' stays flat), then dot traversal
// ({catalyst.name}).
function getPath (context, path) {
    if (context == null || typeof context !== 'object') return undefined;

    const key = keyOf(context, path);
    if (key !== undefined) return context[key];

    const dot = path.indexOf('.');
    if (dot === -1) return undefined;

    const head = keyOf(context, path.slice(0, dot));
    if (head === undefined) return undefined;

    return getPath(context[head], path.slice(dot + 1));
}

// Admin templates reach the client in two shapes: an object whose `layout`
// still needs the Handlebars pass (rendered over the template.* fields), or a
// plain string the backend already ran through Handlebars. Both yield a
// message that still carries single-brace tokens for renderTemplate. Returns
// null when there is nothing renderable (empty string, missing layout,
// compile failure) — callers fall back to their legacy structure.
export function resolveTemplate (template, detail = {}) {
    if (typeof template === 'string') {
        return template.trim() || null;
    }

    if (!template?.layout) return null;

    try {
        return Handlebars.compile(template.layout)({
            ...detail,
            subject: template.subject ?? '',
            action: template.action ?? '',
            object_text: template.object_text || '',
            object_href: template.object_href ?? '',
            cta_text: template.cta_text ?? '',
            cta_href: template.cta_href ?? '',
        });
    } catch (e) {
        return null;
    }
}

// Whether an item carries a renderable admin template. Neuron notifications are
// template-only — an item without one is dropped from the list rather than
// rendered from a local catalog.
export function hasTemplate (detail) {
    return resolveTemplate(detail?.template, detail) !== null;
}

export function renderTemplate (template, context) {
    return String(template ?? '').replace(/\{([^{}]+)\}/g, (match, path) => {
        const value = getPath(context, path.trim());

        // A bare entity token ({challenge}) resolves to an object — an
        // authoring mistake ({challenge.title} was meant). Stays literal, like
        // any unresolved token, so it is visible and fixable in the admin.
        if (value == null || value === '' || typeof value === 'object') {
            return match;
        }

        return String(value);
    });
}
